import { useEffect, useRef, useState } from "react"
import BaseMap, { BaseMapRef } from "./BaseMap"
import JobMarkerLayer from "./layers/JobMarkerLayer"
import MetroLayers from "./layers/MetroLayers"
import TechMarkersLayer from "./layers/TechMarkersLayer"
import RouteLayer from "./layers/RouteLayer"
import TrafficLayer from "./layers/TrafficLayer"
import JobTechsMapControls from "./JobTechsMapControls"
import { useJobTechsView } from "./useJobTechsView"
import { COUNTRY_VIEWS, isRealPosition, zoomForAddress } from "./mapUtils"
import { useHub } from "../../core/Global"
import { HereGeocodeItem } from "../../models/MapModels"
import Technician from "../../modules/technicians/models/technician"
import JobMetro from "../../modules/crm/base/models/JobMetro"
import Country from "../../modules/base/models/Country"

interface IProps {
    /** The address picked in the form; its pin can be dragged to the exact spot. */
    address?: HereGeocodeItem
    /** Where the map opens while there is no address yet. */
    country?: Country
    /** The technicians who may take the job (the form's Technician list). Anybody else is stepped back. */
    techs?: Technician[]
    selectedTech?: Technician
    metros?: JobMetro[]
    selectedMetro?: JobMetro
    onSelectMetro?: (metro: JobMetro) => void
    onSelectTech?: (tech: Technician) => void
    /** The pin was dragged: the address found where it was let go. */
    onAddressMoved?: (address: HereGeocodeItem) => void
    onZoomChanged?: (zoom: number) => void
    /** What the route to the picked technician should say, when the form already says it. */
    routeLabel?: string
    height?: number | string
}

/**
 * The map of the add-job form: the address being picked (its pin dragged to the exact spot),
 * the metros' areas to pick from, and every technician where they are -- those who may take the
 * job in full colour, everybody else stepped back. Picking a technician, here or in the form,
 * draws their driving route to the job with the time and miles on it.
 */
export default function JobFormMap(props: IProps) {

    const jobPosition = isRealPosition(props.address?.position) ? props.address!.position : undefined
    const hub = useHub()
    const mapRef = useRef<BaseMapRef>(null)
    // The views below wait for this: the address may be there before the map is.
    const [mapReady, mapReadySet] = useState<boolean>(false)
    const view = useJobTechsView({ mapRef, mapReady, jobPosition, techs: props.techs, selectedTech: props.selectedTech })
    // The traffic layer: off until asked for -- every tile of it is paid for.
    const [traffic, trafficSet] = useState<boolean>(false)
    // The metros' areas: on until switched off.
    const [metros, metrosSet] = useState<boolean>(true)

    // No address yet: the country the form is for.
    useEffect(() => {
        if (!mapReady || jobPosition || !props.country) return
        const map = mapRef.current?.getMap()
        if (!map) return
        const known = COUNTRY_VIEWS[props.country.alpha2Code?.toLowerCase() ?? ""]
        if (known) map.setView([known.lat, known.lng], known.zoom)
        else if (props.country.alpha3Code) hub.Get("/maps/geocode/country/" + props.country.alpha3Code, res => {
            if (res.success && res.data?.position) mapRef.current?.getMap()?.setView([res.data.position.lat, res.data.position.lng], 4)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady, props.country?.id, !!jobPosition])

    // A new address with no technicians to frame it with yet: go to it, as close as it is precise.
    useEffect(() => {
        if (!mapReady || !jobPosition || props.techs?.length) return
        mapRef.current?.getMap()?.setView([jobPosition.lat, jobPosition.lng], zoomForAddress(props.address!))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady, jobPosition?.lat, jobPosition?.lng])

    const moved = (position: { lat: number, lng: number }) => {
        hub.Get(`/maps/geocode/reverse/?position=${position.lat},${position.lng}`, res => {
            if (res.success && res.data) props.onAddressMoved?.(res.data)
        })
    }

    return (
        <BaseMap ref={mapRef} height={props.height} center={jobPosition} zoom={jobPosition ? 12 : 4}
            onReady={() => mapReadySet(true)}
            onZoomChanged={props.onZoomChanged}>
            {metros && <MetroLayers metros={props.metros} selectedMetroId={props.selectedMetro?.id} onSelect={props.onSelectMetro} labels />}
            <TechMarkersLayer techs={view.allTechs} live
                selectedTechId={props.selectedTech?.id}
                dimmedTechIds={view.dimmed}
                onTechClick={props.onSelectTech} />
            <RouteLayer from={view.selectedPos} to={jobPosition} label={props.routeLabel} onRoute={view.fitRoute} />
            <JobMarkerLayer position={jobPosition} title={props.address?.address?.label} draggable onMoved={moved} />
            {traffic && <TrafficLayer />}
            <JobTechsMapControls
                onJobAndPick={jobPosition ? view.showJobAndPick : undefined}
                onEveryone={jobPosition ? view.showEveryone : undefined}
                metros={metros} onMetros={() => metrosSet(m => !m)}
                traffic={traffic} onTraffic={() => trafficSet(t => !t)} />
        </BaseMap>
    )
}
